import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/store/cart";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/hooks/useSettings";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatCurrency } from "@/lib/format";
import { toast } from "sonner";
import { z } from "zod";
import { Loader2, Banknote, Smartphone, CreditCard, Check } from "lucide-react";
import { cn } from "@/lib/utils";

const baseSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  phone: z.string().trim().min(6, "Phone is required").max(20),
  address: z.string().trim().min(5, "Address is too short").max(500),
});

const bdPhoneSchema = z
  .string()
  .trim()
  .regex(/^(?:\+?880|0)?1[3-9]\d{8}$/, "Enter a valid Bangladeshi phone number");

const cardSchema = z.object({
  card_number: z
    .string()
    .trim()
    .transform((v) => v.replace(/\s+/g, ""))
    .pipe(z.string().regex(/^\d{13,19}$/, "Enter a valid card number")),
  card_expiry: z
    .string()
    .trim()
    .regex(/^(0[1-9]|1[0-2])\/\d{2}$/, "Use MM/YY format"),
  card_cvv: z.string().trim().regex(/^\d{3,4}$/, "Invalid CVV"),
});

type PaymentMethod = "cod" | "bkash" | "nagad" | "card";

const PAYMENT_OPTIONS: {
  id: PaymentMethod;
  label: string;
  description: string;
  icon: typeof Banknote;
}[] = [
  { id: "cod", label: "Cash on Delivery", description: "Pay with cash when your order arrives.", icon: Banknote },
  { id: "bkash", label: "bKash", description: "Pay instantly from your bKash mobile wallet.", icon: Smartphone },
  { id: "nagad", label: "Nagad", description: "Quick mobile payment via your Nagad account.", icon: Smartphone },
  { id: "card", label: "Credit / Debit Card", description: "Visa, Mastercard, Amex — secured by SSLCommerz.", icon: CreditCard },
];

const formatCardNumber = (v: string) =>
  v.replace(/\D/g, "").slice(0, 19).replace(/(.{4})/g, "$1 ").trim();

const formatExpiry = (v: string) => {
  const digits = v.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";
  const navigate = useNavigate();
  const [zones, setZones] = useState<any[]>([]);
  const [zoneId, setZoneId] = useState<string>("");
  const [form, setForm] = useState({
    name: "",
    email: user?.email || "",
    phone: "",
    address: "",
  });
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">("");
  const [walletPhone, setWalletPhone] = useState("");
  const [card, setCard] = useState({ card_number: "", card_expiry: "", card_cvv: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/auth?redirect=/checkout");
      return;
    }
    if (items.length === 0) {
      navigate("/");
      return;
    }
    supabase.from("shipping_zones").select("*").order("charge").then(({ data }) => {
      setZones(data ?? []);
      if (data && data.length) setZoneId(data[0].id);
    });
    setForm((f) => ({ ...f, email: user.email || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const sub = subtotal();
  const zone = zones.find((z) => z.id === zoneId);
  const shipping = zone ? Number(zone.charge) : 0;
  const total = sub + shipping;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      baseSchema.parse(form);
    } catch (err: any) {
      toast.error(err.errors?.[0]?.message || "Invalid input");
      return;
    }
    if (!zoneId) {
      toast.error("Select a shipping zone");
      return;
    }
    if (!paymentMethod) {
      toast.error("Please select a payment method");
      return;
    }

    if (paymentMethod === "bkash" || paymentMethod === "nagad") {
      const result = bdPhoneSchema.safeParse(walletPhone);
      if (!result.success) {
        toast.error(result.error.errors[0]?.message || "Invalid wallet number");
        return;
      }
    }

    if (paymentMethod === "card") {
      const result = cardSchema.safeParse(card);
      if (!result.success) {
        toast.error(result.error.errors[0]?.message || "Invalid card details");
        return;
      }
    }

    setLoading(true);

    const isCod = paymentMethod === "cod";
    const { data: order, error: orderError } = await supabase
      .from("orders")
      .insert({
        user_id: user.id,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        customer_address: form.address,
        shipping_zone_id: zoneId,
        subtotal: sub,
        shipping_charge: shipping,
        total_amount: total,
        payment_status: "pending",
        order_status: isCod ? "confirmed" : "pending",
        payment_method: paymentMethod,
      })
      .select()
      .single();

    if (orderError || !order) {
      setLoading(false);
      toast.error(orderError?.message || "Failed to create order");
      return;
    }

    const itemsPayload = items.map((i) => ({
      order_id: order.id,
      product_id: i.id,
      product_name: i.name,
      quantity: i.quantity,
      price: i.price,
      size: i.size ?? null,
      color: i.color ?? null,
    }));
    await supabase.from("order_items").insert(itemsPayload);

    // Cash on delivery — skip gateway
    if (isCod) {
      clear();
      toast.success("Order placed! Pay on delivery.");
      navigate(`/order-confirmation/${order.id}`);
      setLoading(false);
      return;
    }

    // Mobile wallet / card via SSLCommerz
    try {
      const { data: payRes, error: payErr } = await supabase.functions.invoke("sslcommerz-init", {
        body: { order_id: order.id },
      });
      if (payErr) throw payErr;
      if (payRes?.gateway_url) {
        clear();
        window.location.href = payRes.gateway_url;
        return;
      }
      clear();
      navigate(`/order-confirmation/${order.id}`);
    } catch (err: any) {
      clear();
      toast.warning("Payment gateway unavailable — order saved as pending");
      navigate(`/order-confirmation/${order.id}`);
    }
    setLoading(false);
  };

  const isPayDisabled = loading || !paymentMethod;

  return (
    <div className="container py-6 md:py-12 px-4 md:px-6 max-w-full overflow-x-hidden pb-[calc(2rem+env(safe-area-inset-bottom))]">
      <h1 className="font-display text-2xl md:text-4xl font-bold mb-6 md:mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_400px] gap-6 md:gap-10 min-w-0">
        <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6 min-w-0">
          <div className="rounded-2xl bg-card shadow-soft p-4 md:p-6 space-y-4 min-w-0">
            <h2 className="font-display text-xl font-semibold">Contact & shipping</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="zone">Shipping zone</Label>
                <Select value={zoneId} onValueChange={setZoneId}>
                  <SelectTrigger id="zone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {zones.map((z) => (
                      <SelectItem key={z.id} value={z.id}>{z.zone_name} — {formatCurrency(Number(z.charge), symbol)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="address">Delivery address</Label>
              <Textarea id="address" required rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>

          {/* Payment method */}
          <div className="rounded-2xl bg-card shadow-soft p-4 md:p-6 space-y-4 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-display text-xl font-semibold">Payment method</h2>
              {paymentMethod && (
                <span className="text-xs text-muted-foreground">
                  Selected: <span className="font-medium text-foreground">{PAYMENT_OPTIONS.find((p) => p.id === paymentMethod)?.label}</span>
                </span>
              )}
            </div>

            <RadioGroup
              value={paymentMethod}
              onValueChange={(v) => setPaymentMethod(v as PaymentMethod)}
              className="grid sm:grid-cols-2 gap-3"
            >
              {PAYMENT_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = paymentMethod === opt.id;
                return (
                  <label
                    key={opt.id}
                    htmlFor={`pm-${opt.id}`}
                    className={cn(
                      "relative flex cursor-pointer items-start gap-3 rounded-xl border-2 p-4 transition-all",
                      "hover:border-primary/50 hover:bg-accent/30",
                      selected
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border bg-background",
                    )}
                  >
                    <RadioGroupItem value={opt.id} id={`pm-${opt.id}`} className="sr-only" />
                    <div
                      className={cn(
                        "grid h-10 w-10 shrink-0 place-items-center rounded-lg",
                        selected ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground",
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold leading-tight">{opt.label}</p>
                      <p className="mt-1 text-xs text-muted-foreground leading-snug">{opt.description}</p>
                    </div>
                    {selected && (
                      <span className="absolute top-2 right-2 grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                  </label>
                );
              })}
            </RadioGroup>

            {/* Conditional inputs */}
            {(paymentMethod === "bkash" || paymentMethod === "nagad") && (
              <div className="rounded-xl border border-dashed bg-muted/30 p-4 space-y-2 animate-fade-in">
                <Label htmlFor="wallet-phone">
                  {paymentMethod === "bkash" ? "bKash" : "Nagad"} account number
                </Label>
                <Input
                  id="wallet-phone"
                  inputMode="numeric"
                  placeholder="01XXXXXXXXX"
                  value={walletPhone}
                  onChange={(e) => setWalletPhone(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  You'll be redirected to {paymentMethod === "bkash" ? "bKash" : "Nagad"} to complete payment.
                </p>
              </div>
            )}

            {paymentMethod === "card" && (
              <div className="rounded-xl border border-dashed bg-muted/30 p-4 space-y-3 animate-fade-in">
                <div>
                  <Label htmlFor="card-number">Card number</Label>
                  <Input
                    id="card-number"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="1234 5678 9012 3456"
                    value={card.card_number}
                    onChange={(e) => setCard({ ...card, card_number: formatCardNumber(e.target.value) })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="card-expiry">Expiry (MM/YY)</Label>
                    <Input
                      id="card-expiry"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      placeholder="MM/YY"
                      value={card.card_expiry}
                      onChange={(e) => setCard({ ...card, card_expiry: formatExpiry(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="card-cvv">CVV</Label>
                    <Input
                      id="card-cvv"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      placeholder="123"
                      maxLength={4}
                      value={card.card_cvv}
                      onChange={(e) => setCard({ ...card, card_cvv: e.target.value.replace(/\D/g, "") })}
                      required
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Card details are processed securely via SSLCommerz. We never store your card.
                </p>
              </div>
            )}
          </div>

          <Button
            type="submit"
            variant="hero"
            size="xl"
            className="w-full"
            disabled={isPayDisabled}
          >
            {loading ? (
              <><Loader2 className="h-5 w-5 animate-spin" /> Processing...</>
            ) : !paymentMethod ? (
              "Select a payment method"
            ) : paymentMethod === "cod" ? (
              `Place order — ${formatCurrency(total, symbol)}`
            ) : (
              `Pay ${formatCurrency(total, symbol)}`
            )}
          </Button>
        </form>

        <div className="rounded-2xl bg-card shadow-soft p-4 md:p-6 space-y-4 h-fit lg:sticky lg:top-24 min-w-0">
          <h2 className="font-display text-xl font-semibold">Order summary</h2>
          <div className="space-y-3">
            {items.map((i) => (
              <div key={i.cartId} className="flex gap-3">
                <div className="h-14 w-14 rounded-lg bg-secondary overflow-hidden shrink-0 relative">
                  {i.image_url && <img src={i.image_url} alt={i.name} className="h-full w-full object-cover" />}
                  <span className="absolute -top-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-foreground text-[10px] font-bold text-background">{i.quantity}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{i.name}</p>
                  {(i.size || i.color) && (
                    <p className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                      {i.size && <span>Size: {i.size}</span>}
                      {i.color && (
                        <span className="inline-flex items-center gap-1">
                          Color:
                          <span className="inline-block h-3 w-3 rounded-full ring-1 ring-foreground/15" style={{ background: i.color }} />
                          {i.color}
                        </span>
                      )}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">{formatCurrency(i.price, symbol)} × {i.quantity}</p>
                </div>
                <span className="text-sm font-medium">{formatCurrency(i.price * i.quantity, symbol)}</span>
              </div>
            ))}
          </div>
          <div className="border-t pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatCurrency(sub, symbol)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{formatCurrency(shipping, symbol)}</span></div>
            <div className="flex justify-between font-display text-lg font-bold pt-2 border-t">
              <span>Total</span><span>{formatCurrency(total, symbol)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
